/* eslint-disable no-underscore-dangle */
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Divider,
  Alert,
  IconButton,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useCallback, useMemo, useState } from "react";
import { ReportGmailerrorred, Clear } from "@mui/icons-material";
import { format } from "date-fns";
import { Link } from "react-router-dom";
import { formatPropertyType } from "../../utils";
import { NumericFormatCustom } from "../../components/NumericFormatCustom";
import { IProperty, IPropertyPhotos } from "../../typos/properties";
import { TransactionType } from "../../mocks";
import { updateAdsStatus } from "../../api/ads";
import { Loader } from "../../components/Loader";
import { PropertyStatusTag } from "../../components/PropertyStatusTag";

interface IPropertyViewProps {
  property: IProperty | null;
  onClose(): void;
  onSuccess(): void;
}

export default function PropertyViewDialog({
  property,
  onClose,
  onSuccess,
}: IPropertyViewProps) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("md"));

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");

  const handleStatusChange = useCallback(
    async (ads: { id: string }[], status: string) => {
      setIsLoading(true);

      try {
        await updateAdsStatus(ads, status);
        setIsLoading(false);
        onSuccess();
      } catch (err: any) {
        setError(err.toString());
        setIsLoading(false);
      }
    },
    [onSuccess],
  );

  const propertyAddress = useMemo(() => {
    if (property) {
      const arr = [];

      if (property.address.street) arr.push(property.address.street);
      if (property.address.neighbourhood)
        arr.push(property.address.neighbourhood);
      if (property.address.city) arr.push(property.address.city);
      if (property.address.state) arr.push(property.address.state);

      return arr.join(", ");
    }

    return "";
  }, [property]);

  if (!property) return <> </>;

  return (
    <Dialog
      open
      onClose={onClose}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      fullWidth
      fullScreen={fullScreen}
    >
      {isLoading && <Loader />}

      <DialogTitle id="alert-dialog-title">
        <div className="flex items-center">
          {`Detalles de la publicación cód. #${property?.code}`}
        </div>
      </DialogTitle>

      <IconButton className="!absolute right-3 top-3" onClick={onClose}>
        <Clear className="text-secondary" />
      </IconButton>

      <DialogContent dividers>
        <div className="w-full flex flex-col gap-4">
          {error && (
            <Alert
              icon={<ReportGmailerrorred fontSize="inherit" />}
              severity="error"
            >
              {error}
            </Alert>
          )}

          <div>
            <h2 className="w-full text-lg font-bold flex items-center">
              {`${formatPropertyType({
                propertyType: property.type,
                isResidentialComplex: property.isResidentialComplex,
                floorsCount: property.floorsCount,
              })} en ${TransactionType[property.transactionType].toLowerCase()}`}
            </h2>
            <p className="text-slate-500 text-sm">{propertyAddress}</p>
            <small className="text-xs">
              {"Actualizado en "}
              {property.updatedAt
                ? format(property.updatedAt, "dd/MM/yyyy")
                : "-"}
            </small>

            <div className="mt-2">
              <PropertyStatusTag status={property.status} />
            </div>
          </div>

          <Divider />

          {property.photos && property.photos.length > 0 && (
            <>
              <ul className="flex flex-wrap w-full gap-3">
                {property.photos.map((item: IPropertyPhotos) => (
                  <li
                    key={item.name}
                    className="aspect-square w-full h-full max-w-32 rounded-md overflow-hidden"
                  >
                    <figure className="aspect-square w-full h-full">
                      <img
                        src={item.thumbnailUrl}
                        alt=""
                        className="bg-gray-200 w-full h-full object-contain"
                      />
                    </figure>
                  </li>
                ))}
              </ul>

              <Divider />
            </>
          )}

          {(property.videoUrl || property.tourUrl) && (
            <>
              <ul className="flex flex-col gap-1">
                {property.videoUrl && (
                  <li>
                    <Link
                      to={property.videoUrl}
                      target="_blank"
                      className="link text-sm"
                      rel="noreferrer"
                    >
                      {property.videoUrl}
                    </Link>
                  </li>
                )}

                {property.tourUrl && (
                  <li>
                    <Link
                      to={property.tourUrl}
                      target="_blank"
                      className="link text-sm"
                      rel="noreferrer"
                    >
                      {property.tourUrl}
                    </Link>
                  </li>
                )}
              </ul>

              <Divider />
            </>
          )}

          <p className="text-sm">
            <strong>{"Descripción: "}</strong>
            {property.description
              ? property.description
              : "El anunciante no incluyó una descripción."}
          </p>

          <Divider />

          <div className="flex flex-col gap-1">
            <span className="text-2xl text-primary font-bold">
              <NumericFormatCustom
                displayType="text"
                name="price"
                value={property.price}
              />
            </span>
          </div>
        </div>
      </DialogContent>

      <DialogActions>
        <Button
          onClick={() =>
            handleStatusChange([{ id: property.id }], "PAUSED_BY_APPLICATION")
          }
          variant="contained"
          color="error"
          disableElevation
        >
          Rechazar
        </Button>

        <Button
          onClick={() => handleStatusChange([{ id: property.id }], "ACTIVE")}
          variant="contained"
          color="success"
          disableElevation
        >
          Aprobar
        </Button>
      </DialogActions>
    </Dialog>
  );
}
